import React from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { CtaBlock } from "@/components/CtaBlock";
import NotFound from "@/pages/public/NotFound";
import { ShieldCheck, BadgeCheck, ArrowRight, Clock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

const GuideCard = ({ g }) => (
  <Link to={`/guide/${g.slug}`} className="rounded-xl bg-white border border-slate-200 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`author-guide-${g.slug}`}>
    <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">{g.category}</span>
    <h3 className="mt-2 text-base font-bold text-slate-900 leading-snug" style={{ fontFamily: "Outfit" }}>{g.title}</h3>
    <div className="mt-3 flex items-center justify-between text-sm">
      <span className="text-slate-500 flex items-center gap-1"><Clock size={14} /> {g.reading_time} min</span>
      <span className="text-emerald-800 font-semibold flex items-center gap-1">Leggi <ArrowRight size={14} /></span>
    </div>
  </Link>
);

export default function AuthorPage() {
  const { slug } = useParams();
  const { data: a, isLoading, isError } = useQuery({
    queryKey: ["author", slug],
    queryFn: () => api.get(`/authors/${slug}`).then((r) => r.data),
    retry: false,
  });

  if (isLoading) return <div className="max-w-4xl mx-auto px-4 py-20 text-slate-500">Caricamento...</div>;
  if (isError || !a) return <NotFound />;

  const initials = a.name.split(" ").filter(Boolean).slice(-2).map((w) => w[0]).join("").toUpperCase();
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Person", name: a.name, jobTitle: a.role,
    description: a.bio, knowsAbout: a.expertise,
    worksFor: { "@type": "Organization", name: "SENIORCAR" },
  };

  return (
    <>
      <Seo title={`${a.name} — ${a.role} | SENIORCAR`} description={a.bio?.slice(0, 155)} jsonLd={jsonLd} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        <Breadcrumbs items={[{ label: "Autori" }, { label: a.name }]} />
      </div>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-6" data-testid="author-profile">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start">
          <div className="h-20 w-20 rounded-2xl flex items-center justify-center text-white text-2xl font-extrabold shrink-0"
            style={{ backgroundColor: "var(--sc-emerald)", fontFamily: "Outfit" }}>{initials}</div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit" }}>{a.name}</h1>
              {a.is_reviewer
                ? <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-800 bg-emerald-100 px-2.5 py-1 rounded-full"><ShieldCheck size={13} /> Revisore</span>
                : <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-800 bg-emerald-100 px-2.5 py-1 rounded-full"><BadgeCheck size={13} /> Autore</span>}
            </div>
            <p className="mt-1 font-semibold text-emerald-800">{a.role}</p>
            <p className="mt-4 text-slate-600 leading-relaxed">{a.bio}</p>
            {a.credentials && <p className="mt-3 text-sm text-slate-500 italic">{a.credentials}</p>}
            {a.expertise?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="author-expertise">
                {a.expertise.map((e) => (
                  <span key={e} className="text-xs font-semibold text-slate-700 border border-slate-300 rounded-full px-3 py-1">{e}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {a.authored?.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-10">
          <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>Articoli scritti ({a.authored.length})</h2>
          <div className="grid sm:grid-cols-2 gap-4">{a.authored.map((g) => <GuideCard key={g.slug} g={g} />)}</div>
        </section>
      )}

      {a.reviewed?.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-10">
          <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>Articoli revisionati ({a.reviewed.length})</h2>
          <div className="grid sm:grid-cols-2 gap-4">{a.reviewed.map((g) => <GuideCard key={g.slug} g={g} />)}</div>
        </section>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 my-14"><CtaBlock variant="test" /></div>
    </>
  );
}
